import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { MapPin, Home, Send, Inbox, Check } from 'lucide-react';
import LoopVideo, { filmSrc } from './elements/loopVideo.jsx';
import ReelViewer from './elements/reelViewer.jsx';
import LivePicker from './elements/livePicker.jsx';
import { listSentShares, listReceivedShares, liveCounts } from './elements/sharesApi.jsx';
import './share.scss';

// تبِ «شیر» — دو بخش:
//   «فرستاده‌ها»: هر لینکی که این کاربر فرستاده — همان لحظه‌ی فرستادن (با «هنوز دیده نشده»)؛ وقتی گیرنده باز کرد:
//   «فایلِ ارسال‌شده توسطِ گیرنده دیده شد» (چه کسانی، کِی، و اگر الان کسی داخلِ خانه است)
//   «دریافتی‌ها»: لینک‌هایی که دیگران فرستاده‌اند و این کاربر بازشان کرده
// «بازدیدِ مجدد» = ورود به همان خانه با همان لینک (بازدیدِ مشترک: هر کس الان داخل باشد را می‌بینید و صدایش را می‌شنوید)

const DesignWalk = lazy(() => import('./elements/designWalk.jsx'));
const fa = (n) => Number(n || 0).toLocaleString('fa-IR');

function formatVisitors(names, total) {
  const known = names.filter(Boolean);
  if (!known.length) return total > 1 ? `${fa(total)} نفر باز کردند` : 'یک نفر باز کرد';
  const extra = total - Math.min(2, known.length);
  if (known.length === 1) return extra > 0 ? `با ${known[0]} و ${fa(extra)} نفر دیگر` : `با ${known[0]}`;
  return extra > 0 ? `با ${known[0]}، ${known[1]} و ${fa(extra)} نفر دیگر` : `با ${known[0]} و ${known[1]}`;
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return 'چند لحظه پیش';
  if (diffMin < 60) return `${fa(diffMin)} دقیقه پیش`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${fa(diffHour)} ساعت پیش`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return 'دیروز';
  if (diffDay <= 6) return `${fa(diffDay)} روز پیش`;
  return date.toLocaleDateString('fa-IR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function priceText(l) {
  if (!l) return '';
  if (l.dealType === 'rent') {
    const parts = [];
    if (l.deposit) parts.push(`ودیعه ${fa(l.deposit)}`);
    if (l.rent) parts.push(`اجاره ${fa(l.rent)}`);
    return parts.length ? `${parts.join(' - ')} تومان` : '';
  }
  return l.price ? `${fa(l.price)} تومان` : '';
}

function Avatars({ names }) {
  const shown = names.slice(0, 3);
  return (
    <div className="share-page__avatars">
      {shown.map((name, i) => (
        <span key={i} className="share-page__avatar" style={{ zIndex: shown.length - i }}>
          {name ? name.charAt(0) : '؟'}
        </span>
      ))}
    </div>
  );
}

function ShareCard({ item, mode, liveCount, onOpen, onRevisit }) {
  const d = item.design;
  const l = d.listing || {};
  const names = mode === 'sent' ? item.visitors.map((v) => v.name) : [item.senderName];
  return (
    <article className="share-page__card">
      <button type="button" className="share-page__main" onClick={onOpen}>
        <div className="share-page__thumb">
          {d.filmUrl ? (
            <LoopVideo src={filmSrc(d, true)} poster={d.thumbUrl} threshold={0.5} />
          ) : d.thumbUrl ? (
            <img src={d.thumbUrl} alt="" loading="lazy" />
          ) : (
            <Home size={26} strokeWidth={1.4} />
          )}
        </div>
        <div className="share-page__card-body">
          <h3>{l.title || 'خانه‌ی سه‌بعدی'}</h3>
          {priceText(l) && <p className="share-page__price">{priceText(l)}</p>}
          <p className="share-page__meta">
            {[l.area && `${fa(l.area)} متر`, l.rooms > 0 && `${fa(l.rooms)} اتاق`, l.buildYear > 0 && `${fa(l.buildYear)} سال ساخت`]
              .filter(Boolean)
              .join(' • ')}
          </p>
          {l.province && (
            <span className="share-page__location">
              <MapPin size={12} strokeWidth={2} />
              {l.province}
            </span>
          )}
        </div>
      </button>

      {mode === 'sent' && (
        <p className={`share-page__seen ${item.seen ? 'is-seen' : ''}`}>
          {item.seen ? <Check size={14} strokeWidth={2.4} /> : <Send size={13} strokeWidth={2.2} />}
          <span>
            {item.seen ? 'فایلِ ارسال‌شده توسطِ گیرنده دیده شد' : 'ارسال شد — گیرنده هنوز باز نکرده'}
          </span>
          <small>{formatRelativeTime(item.seen ? item.lastVisitAt : item.sentAt)}</small>
        </p>
      )}
      <div className="share-page__visit">
        <div className="share-page__visit-info">
          {(mode !== 'sent' || item.seen) && <Avatars names={names} />}
          <div className="share-page__visit-text">
            <span>
              {mode === 'sent'
                ? item.seen
                  ? formatVisitors(names, item.visitorCount)
                  : 'منتظرِ بازدیدِ گیرنده'
                : item.senderName
                  ? `از طرفِ ${item.senderName}`
                  : 'لینکِ دریافتی'}
            </span>
            {liveCount > 0 ? (
              <span className="share-page__live">
                <i />
                {`الان ${fa(liveCount)} نفر داخلِ خانه‌اند`}
              </span>
            ) : (
              <span className="share-page__visit-time">{formatRelativeTime(item.lastVisitAt || item.sentAt)}</span>
            )}
          </div>
        </div>
        <button type="button" className={`share-page__revisit-btn ${liveCount > 0 ? 'is-live' : ''}`} onClick={onRevisit}>
          {liveCount > 0 ? 'پیوستن' : 'بازدید مجدد'}
        </button>
      </div>
    </article>
  );
}

function SharePage() {
  const [tab, setTab] = useState('sent');
  const [data, setData] = useState({ sent: null, received: null });
  const [error, setError] = useState('');
  const [live, setLive] = useState({});
  const [reel, setReel] = useState(null); // { items, index }
  const [picker, setPicker] = useState(null); // design (با shareCode)
  const [walk, setWalk] = useState(null); // { design, live }

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, r] = await Promise.all([listSentShares(), listReceivedShares()]);
      setData({ sent: s.items || [], received: r.items || [] });
    } catch (e) {
      setError(e.message);
      setData((prev) => ({ sent: prev.sent || [], received: prev.received || [] }));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // چند نفر الان داخلِ هر خانه‌اند — هر ۱۵ ثانیه (فقط وقتی صفحه دیده می‌شود)
  const items = data[tab] || [];
  const codesKey = [...(data.sent || []), ...(data.received || [])].map((x) => x.code).join(',');
  useEffect(() => {
    if (!codesKey) return undefined;
    let stop = false;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      const r = await liveCounts(codesKey.split(','));
      if (!stop) setLive(r);
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [codesKey, walk]);

  const withCode = (it) => ({ ...it.design, shareCode: it.code });

  return (
    <div className="share-page">
      <nav className="share-page__tabs">
        <button type="button" className={tab === 'sent' ? 'is-active' : ''} onClick={() => setTab('sent')}>
          <Send size={16} strokeWidth={1.9} />
          <span>فرستاده‌ها</span>
          {data.sent && data.sent.length > 0 && <em>{fa(data.sent.length)}</em>}
        </button>
        <button type="button" className={tab === 'received' ? 'is-active' : ''} onClick={() => setTab('received')}>
          <Inbox size={16} strokeWidth={1.9} />
          <span>دریافتی‌ها</span>
          {data.received && data.received.length > 0 && <em>{fa(data.received.length)}</em>}
        </button>
      </nav>

      <section className="share-page__list">
        {data[tab] === null &&
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="share-page__card is-loading" />)}
        {data[tab] !== null && items.length === 0 && (
          <div className="share-page__empty">
            {tab === 'sent' ? <Send size={30} strokeWidth={1.3} /> : <Inbox size={30} strokeWidth={1.3} />}
            <p>
              {tab === 'sent'
                ? 'هنوز لینکی نفرستاده‌اید. در اکسپلور روی «اشتراک» بزنید و لینک را بفرستید؛ همین‌جا می‌آید و وقتی گیرنده بازش کرد خبردار می‌شوید.'
                : 'هنوز لینکی از کسی باز نکرده‌اید.'}
            </p>
            {error && (
              <button type="button" onClick={load}>
                تلاشِ دوباره
              </button>
            )}
          </div>
        )}
        {items.map((it, i) => (
          <ShareCard
            key={it.code}
            item={it}
            mode={tab}
            liveCount={live[it.code] || 0}
            onOpen={() => setReel({ items: items.map(withCode), index: i })}
            onRevisit={() => setPicker(withCode(it))}
          />
        ))}
      </section>

      {reel && <ReelViewer items={reel.items} startIndex={reel.index} onClose={() => setReel(null)} />}
      {picker && (
        <LivePicker
          code={picker.shareCode}
          design={picker}
          onClose={() => setPicker(null)}
          onStart={(session, name) => {
            setWalk({ design: picker, live: { session, name } });
            setPicker(null);
          }}
        />
      )}
      {walk && (
        <Suspense fallback={null}>
          <DesignWalk
            design={walk.design}
            live={walk.live}
            onClose={() => {
              setWalk(null);
              load();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

export default SharePage;
